import type { ComponentType } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRightIcon,
  CloudIcon,
  CodeIcon,
  LayersIcon,
  PenIcon,
  RouteIcon,
  ServerIcon,
} from "../components/icons";
import { usePageMeta } from "../hooks/usePageMeta";
import {
  capabilities,
  engagements,
  features,
  processSteps,
  site,
  stats,
} from "../lib/site";

type IconComponent = ComponentType<{ size?: number; className?: string }>;

const capabilityIcons: Record<string, IconComponent> = {
  frontend: CodeIcon,
  backend: ServerIcon,
  fullstack: LayersIcon,
  infrastructure: CloudIcon,
  design: PenIcon,
  delivery: RouteIcon,
};

export const HomePage = () => {
  usePageMeta(
    "CatalyxLabs — Product, Platform & Infrastructure Studio",
    "CatalyxLabs designs and builds websites, apps, and platforms. Frontend, backend, infrastructure, design, and project management under one roof.",
  );

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <span className="eyebrow">Design · Engineering · Infrastructure</span>
          <h1 className="hero__title">
            Software teams that <span className="hero__accent">finish</span> what
            they start.
          </h1>
          <p className="hero__lede">
            CatalyxLabs is a product studio for companies that need a website, an
            app, or a platform built properly. Design, frontend, backend,
            infrastructure, and delivery all sit on the same team — so you brief
            one group and get a working product.
          </p>

          <div className="hero__actions">
            <Link to="/contact" className="btn btn--primary btn--lg">
              Book a consultation
              <ArrowRightIcon className="btn__icon" />
            </Link>
            {features.portfolio ? (
              <Link to="/portfolio" className="btn btn--secondary btn--lg">
                See our work
              </Link>
            ) : (
              <a href="#capabilities" className="btn btn--secondary btn--lg">
                What we do
              </a>
            )}
          </div>

          <ul className="hero__stats" role="list">
            {stats.map((stat) => (
              <li key={stat.label}>
                <p className="hero__stat-value">{stat.value}</p>
                <p className="hero__stat-label">{stat.label}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section--sunken" id="capabilities">
        <div className="container">
          <div className="section__head">
            <span className="eyebrow">What we do</span>
            <h2>Every discipline a product needs, in one team</h2>
            <p className="lede">
              Most projects stall in the gaps between specialists. We staff all
              six disciplines in-house, so there are no handoffs to manage and no
              vendor pointing at another vendor.
            </p>
          </div>

          <div className="grid grid--3">
            {capabilities.map((capability) => {
              const Icon = capabilityIcons[capability.id] ?? CodeIcon;
              return (
                <article
                  key={capability.id}
                  className="card card--hoverable capability"
                >
                  <span className="capability__icon">
                    <Icon />
                  </span>
                  <h3 className="card__title">{capability.title}</h3>
                  <p className="card__body">{capability.description}</p>
                  <ul className="capability__list">
                    {capability.skills.map((skill) => (
                      <li key={skill} className="capability__chip">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section" id="process">
        <div className="container">
          <div className="section__head">
            <span className="eyebrow">How we work</span>
            <h2>From first call to launch, without the mystery</h2>
            <p className="lede">
              You will always know what is being built this week, what it costs,
              and what is at risk.
            </p>
          </div>

          <ol className="process">
            {processSteps.map((step) => (
              <li key={step.title} className="process__step">
                <h3 className="process__title">{step.title}</h3>
                <p className="process__body">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section--sunken" id="engagements">
        <div className="container">
          <div className="section__head">
            <span className="eyebrow">Ways to work together</span>
            <h2>Pick the shape that fits your team</h2>
          </div>

          <div className="engagements">
            {engagements.map((engagement) => (
              <article key={engagement.title} className="card card--hoverable">
                <h3 className="card__title">{engagement.title}</h3>
                <p className="card__body">{engagement.description}</p>
                <p className="card__body">
                  <strong>Best for:</strong> {engagement.bestFor}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="cta">
            <span className="eyebrow">Let&apos;s talk</span>
            <h2>Tell us what you&apos;re trying to build</h2>
            <p className="cta__lede">
              Send over an idea, a rough sketch, or a problem you haven&apos;t
              solved yet. We&apos;ll reply {site.responseTime} with honest
              thoughts on scope, cost, and whether we&apos;re the right fit.
            </p>

            <div className="cta__actions">
              <Link to="/contact" className="btn btn--primary btn--lg">
                Start a project
                <ArrowRightIcon className="btn__icon" />
              </Link>
              <a
                href={`mailto:${site.email}`}
                className="btn btn--secondary btn--lg"
              >
                Email us directly
              </a>
            </div>

            <p className="cta__direct">
              {site.email} · {site.location}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};
